import { Application, Ticker, UPDATE_PRIORITY } from 'pixi.js';
import { Live2DModel } from 'untitled-pixi-live2d-engine/cubism';

let app: Application | null = null;
let model: Live2DModel | null = null;
let paramIndexMap: Map<string, number> | null = null;

// Parameter overrides applied every frame AFTER model update
const paramOverrides: Map<string, number> = new Map();

export async function initAvatar(container: HTMLElement): Promise<void> {
  app = new Application();
  await app.init({
    width: 1920,
    height: 1080,
    backgroundAlpha: 0,
    preference: 'webgl',
    antialias: true,
    resolution: Math.min(window.devicePixelRatio, 2),
    autoDensity: true,
  });

  container.appendChild(app.canvas);

  model = await Live2DModel.from(`${import.meta.env.BASE_URL}models/hiyori/Hiyori.model3.json`);
  app.stage.addChild(model);

  model.anchor.set(0.5, 0.5);
  model.x = 1920 / 2;
  model.y = 1080 * 0.55;

  const scale = Math.min(1920 / model.width, 1080 / model.height) * 0.8;
  model.scale.set(scale);

  buildParamMap();
  setupOverrideTicker();
  enableDrag(model);
  startIdleLoop();
}

function buildParamMap(): void {
  if (!model) return;
  paramIndexMap = new Map();
  try {
    const coreModel = (model.internalModel as any).coreModel;
    const params = coreModel._model.parameters;
    for (let i = 0; i < params.count; i++) {
      paramIndexMap.set(params.ids[i], i);
    }
    console.log(`[avatar] Mapped ${paramIndexMap.size} parameters`);
  } catch (err) {
    console.warn('[avatar] Could not build param map:', err);
  }
}

function setupOverrideTicker(): void {
  // LOW priority runs AFTER model's NORMAL priority update,
  // so our overrides win over motion system values
  Ticker.shared.add(() => {
    if (!model || paramOverrides.size === 0) return;
    try {
      const coreModel = (model.internalModel as any).coreModel;
      for (const [param, value] of paramOverrides) {
        const index = paramIndexMap?.get(param);
        if (index !== undefined) {
          coreModel.setParameterValueByIndex(index, value);
        }
      }
    } catch {}
  }, undefined, UPDATE_PRIORITY.LOW);
}

export function setParamOverride(name: string, value: number): void {
  paramOverrides.set(name, value);
}

export function clearParamOverride(name: string): void {
  paramOverrides.delete(name);
}

export function clearAllOverrides(): void {
  paramOverrides.clear();
}

export function getParamValue(name: string): number {
  if (!model || !paramIndexMap) return 0;
  const index = paramIndexMap.get(name);
  if (index === undefined) return 0;
  try {
    const coreModel = (model.internalModel as any).coreModel;
    return coreModel.getParameterValueByIndex(index);
  } catch {
    return 0;
  }
}

export function stopMotions(): void {
  if (!model) return;
  try {
    (model.internalModel as any).motionManager.stopAllMotions();
  } catch {}
}

export function restartIdle(): void {
  startIdleLoop();
}

function enableDrag(target: Live2DModel): void {
  target.eventMode = 'static';
  target.cursor = 'grab';

  let dragging = false;
  let offsetX = 0;
  let offsetY = 0;

  target.on('pointerdown', (e: any) => {
    dragging = true;
    target.cursor = 'grabbing';
    const pos = e.global;
    offsetX = target.x - pos.x;
    offsetY = target.y - pos.y;
  });

  target.on('pointermove', (e: any) => {
    if (!dragging) return;
    const pos = e.global;
    target.x = pos.x + offsetX;
    target.y = pos.y + offsetY;
  });

  target.on('pointerup', () => {
    dragging = false;
    target.cursor = 'grab';
  });

  target.on('pointerupoutside', () => {
    dragging = false;
    target.cursor = 'grab';
  });

  // Scroll to resize
  if (app) {
    app.canvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (!model) return;
      const delta = e.deltaY > 0 ? 0.95 : 1.05;
      model.scale.set(model.scale.x * delta);
    }, { passive: false });
  }
}

function startIdleLoop(): void {
  if (!model) return;
  try {
    model.motion('idle', 0);
  } catch {
    // Idle motion may not exist in model
  }
}

export function getModel(): Live2DModel | null {
  return model;
}

export function getApp(): Application | null {
  return app;
}

export function setExpression(name: string): void {
  if (!model) return;
  try {
    model.expression(name);
  } catch {
    // Expression may not exist, fall back silently
  }
}

export function playMotion(group: string, index: number): void {
  if (!model) return;
  try {
    model.motion(group, index);
  } catch {
    // Motion may not exist
  }
}
